/**
 * Utility functions for processing submission activity data
 */

import { 
	SPIKE_DETECTION_DEFAULTS, 
	UI_CONSTANTS, 
	TIME_CONSTANTS 
} from './submissionActivityConstants';

/**
 * Aggregates submission data by date for heatmap visualization
 * @param {Array} submissions - Raw submission data from API
 * @param {number} days - Number of days to include (default: 30)
 * @returns {Array} Processed data for heatmap display
 */
export const aggregateSubmissionsByDate = (submissions, days = UI_CONSTANTS.defaultDaysRange) => {
	const data = [];
	const today = new Date();
	
	// Create date buckets for the specified number of days
	const dateBuckets = new Map();
	
	// Initialize all dates with zero counts
	for (let i = days - 1; i >= 0; i--) {
		const date = new Date(today);
		date.setDate(date.getDate() - i);
		const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
		dateBuckets.set(dateKey, {
			date: new Date(date),
			count: 0,
			submissions: []
		});
	}
	
	// Aggregate submissions by date
	submissions.forEach(submission => {
		const submissionDate = new Date(submission.submitted_at);
		const dateKey = submissionDate.toISOString().split('T')[0];
		
		if (dateBuckets.has(dateKey)) {
			const bucket = dateBuckets.get(dateKey);
			bucket.count++;
			bucket.submissions.push(submission);
		}
	});
	
	// Convert to array and add metadata
	dateBuckets.forEach((bucket, dateKey) => {
		data.push({
			...bucket,
			dayLabel: bucket.date.toLocaleDateString('en-US', { weekday: 'short' }),
			dateLabel: bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
		});
	});
	
	return data;
};

/**
 * Enhanced spike detection algorithm for submission activity
 * @param {Array} activityData - Aggregated activity data
 * @param {Object} options - Detection options
 * @returns {Array} Activity data with spike indicators
 */
export const detectActivitySpikes = (activityData, options = {}) => {
	const {
		spikeThresholdMultiplier = SPIKE_DETECTION_DEFAULTS.spikeThresholdMultiplier,
		minimumSpikeCount = SPIKE_DETECTION_DEFAULTS.minimumSpikeCount,
		rollingWindowDays = SPIKE_DETECTION_DEFAULTS.rollingWindowDays
	} = options;
	
	// Calculate rolling averages for better spike detection
	const dataWithSpikes = activityData.map((day, index) => {
		// Calculate rolling average for context
		const windowStart = Math.max(0, index - Math.floor(rollingWindowDays / 2));
		const windowEnd = Math.min(activityData.length, index + Math.floor(rollingWindowDays / 2) + 1);
		const windowData = activityData.slice(windowStart, windowEnd);
		const rollingAverage = windowData.reduce((sum, d) => sum + d.count, 0) / windowData.length;
		
		// Detect spike
		const isSpike = day.count >= minimumSpikeCount && 
		                day.count >= (rollingAverage * spikeThresholdMultiplier);
		
		return {
			...day,
			isSpike,
			rollingAverage,
			spikeRatio: rollingAverage > 0 ? day.count / rollingAverage : 0
		};
	});
	
	return dataWithSpikes;
};

/**
 * Calculate activity statistics for dashboard display
 * @param {Array} activityData - Processed activity data
 * @returns {Object} Statistics summary
 */
export const calculateActivityStats = (activityData) => {
	if (!activityData || activityData.length === 0) {
		return {
			totalSubmissions: 0,
			avgDaily: 0,
			maxDaily: 0,
			minDaily: 0,
			spikeCount: 0,
			todayCount: 0,
			yesterdayCount: 0,
			percentChange: null,
			useAbsoluteChange: false
		};
	}

	const counts = activityData.map(d => d.count);
	const totalSubmissions = counts.reduce((sum, count) => sum + count, 0);
	const avgDaily = totalSubmissions / activityData.length;
	const maxDaily = Math.max(...counts);
	const minDaily = Math.min(...counts);
	const spikeCount = activityData.filter(d => d.isSpike).length;

	// Get today and yesterday stats (assuming data is chronologically ordered)
	const todayCount = activityData[activityData.length - 1]?.count || 0;
	const yesterdayCount = activityData[activityData.length - 2]?.count || 0;

	let percentChange = null;
	let useAbsoluteChange = false;

	if (yesterdayCount > 0) {
		percentChange = ((todayCount - yesterdayCount) / yesterdayCount) * 100;
	} else if (todayCount > 0) {
		// When yesterday was 0 but today has submissions, show absolute count
		percentChange = todayCount;
		useAbsoluteChange = true;
	} else {
		percentChange = 0; // Both days zero
	}

	return {
		totalSubmissions,
		avgDaily: Math.round(avgDaily * 10) / 10, // Round to 1 decimal
		maxDaily,
		minDaily,
		spikeCount,
		todayCount,
		yesterdayCount,
		percentChange,
		useAbsoluteChange
	};
};

/**
 * Format date range for API calls
 * @param {number} days - Number of days back from today
 * @returns {Object} Formatted start and end dates
 */
export const getDateRangeForDays = (days = UI_CONSTANTS.defaultDaysRange) => {
	const endDate = new Date();
	const startDate = new Date();
	startDate.setDate(startDate.getDate() - (days - 1));
	
	// Format as YYYY-MM-DD for API
	const formatDate = (date) => {
		return date.toISOString().split('T')[0];
	};
	
	return {
		startDate: formatDate(startDate),
		endDate: formatDate(endDate)
	};
};

/**
 * Validate and sanitize submission data
 * @param {Array} submissions - Raw submission data
 * @returns {Array} Cleaned submission data
 */
export const validateSubmissionData = (submissions) => {
	if (!Array.isArray(submissions)) {
		console.warn('Invalid submission data: expected array');
		return [];
	}
	
	return submissions.filter(submission => {
		// Ensure required fields exist
		if (!submission.submitted_at) {
			console.warn('Submission missing submitted_at field:', submission);
			return false;
		}
		
		// Validate date format
		const submissionDate = new Date(submission.submitted_at);
		if (isNaN(submissionDate.getTime())) {
			console.warn('Invalid submission date:', submission.submitted_at);
			return false;
		}
		
		return true;
	});
};